import { Button, Col, Form, ListGroup, Modal, OverlayTrigger, Row, Tooltip } from "react-bootstrap";

import { faPenToSquare, faRefresh, faSpinner, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { PatientVisit } from "../../../../amplify/api-gateway/search-patients";
import { callWrite, LLM_DASHBOARD_UPLOAD_CONTEXT, SCAN_MULTI } from "../../../../amplify/api-gateway/write";

import { dashboardLLMCache } from "../cache";
import './AdditionalContextModal.scss';
import { callDashboardLLM } from "../../../../amplify/api-gateway/dashboard-llm";

/**
 * Modal allow users to add additional context for the user
 */
export const AdditionalContextModal = (props: {
    regenerate: (shouldReloadContext: boolean, target?: string) => void,
    showModal: boolean,
    setShowModal: any,
    patientVisit: PatientVisit,
    tenant: any,
    target?: string,
}) => {

    const [submitting, setSubmitting] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [newContext, setNewContext] = useState("");
    const [isLoadingContext, setIsLoadingContext] = useState(false);
    const [shouldReloadContext, setShouldReloadContext] = useState(true);
    const [isSavingContext, setIsSavingContext] = useState(false);
    const [context, setContext] = useState({
        "contexts": [],
    });
    const [origContext, setOrigContext] = useState({
        "contexts": [],
    });

    useEffect(() => {
        if (props.showModal) {
            setIsLoadingContext(true);
            dashboardLLMCache.get("context", props.patientVisit, props.tenant)
                .then((existingContext) => {
                    if (existingContext) {
                        setOrigContext(JSON.parse(JSON.stringify(existingContext)));
                        setContext(existingContext);
                    }
                })
                .finally(() => {
                    setIsLoadingContext(false);
                });
        }
    }, [props.showModal]);

    const saveContext = async () => {
        const updatedContext: any = JSON.parse(JSON.stringify(context));
        if (newContext) {
            updatedContext["contexts"].push({
                "text": newContext,
                "enabled": true,
            });
        }
        setIsSavingContext(true);
        callWrite({
            operation: LLM_DASHBOARD_UPLOAD_CONTEXT,
            csn: props.patientVisit.csn,
            mrn: props.patientVisit.mrn,
            data: updatedContext,
        })
        .then(() => {
            setContext(updatedContext);
            setOrigContext(updatedContext);
            setNewContext("");
        })
        .catch(() => {
            console.error("Could not save context");
        })
        .finally(() => {
            setIsSavingContext(false);
            setIsEditing(false);
        });
    };

    const handleToggle = (idx: number) => {
        const updatedContext: any = JSON.parse(JSON.stringify(context));
        if (idx < context.contexts.length) {
            updatedContext.contexts[idx].enabled = !updatedContext.contexts[idx].enabled;
        }
        setContext(updatedContext);
    }

    const deleteContext = (idx: number) => {
        const updatedContext = JSON.parse(JSON.stringify(context));
        if (idx < context.contexts.length) {
            updatedContext.contexts.splice(idx, 1);
        }
        setContext(updatedContext);
    }

    const cancelEdits = () => {
        setContext(JSON.parse(JSON.stringify(origContext)));
        setIsEditing(false);
    }

    const handleClose = () => {
        props.setShowModal(false);
    };

    const regenerate = (target?: string) => {
        props.regenerate(shouldReloadContext, target);
        props.setShowModal(false);
    };

    return (
        <Modal show={props.showModal} onHide={handleClose} size="lg">
            <Modal.Body className="p-4">
                <Row>
                    <Col>
                        <strong>Generate Clinical Summary</strong>
                    </Col>
                </Row>
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label><small className="text-muted">Optionally include additional information when regenerating summary. Regeneration may take a few minutes.</small></Form.Label>

                        <ListGroup>
                            {
                                context.contexts.map((ctx: any, idx) => (
                                    <ListGroup.Item>
                                        <Row>
                                            <Col md={1} className="text-right">
                                                <OverlayTrigger
                                                    placement="bottom"
                                                    overlay={
                                                        <Tooltip>
                                                            {ctx.enabled ? <>Include in regeneration</> : <>Exclude in regeneration</>}
                                                        </Tooltip>
                                                    }
                                                >
                                                    <Form.Check type="switch" checked={ctx.enabled} disabled={!isEditing} onChange={() => {handleToggle(idx)}} />
                                                </OverlayTrigger>
                                            </Col>
                                            <Col md={isEditing ? 10 : 11} className="context-text">
                                                {
                                                    ctx.text
                                                }
                                            </Col>
                                            {
                                                isEditing && (
                                                    <Col md={1} className="text-right">
                                                        <OverlayTrigger
                                                            placement="bottom"
                                                            overlay={
                                                                <Tooltip>
                                                                    Delete patient context
                                                                </Tooltip>
                                                            }
                                                        >
                                                            <span onClick={() => {deleteContext(idx)}} className="context-link-btn">
                                                                <FontAwesomeIcon icon={faTimes} />
                                                            </span>
                                                        </OverlayTrigger>
                                                    </Col>
                                                )
                                            }
                                        </Row>
                                    </ListGroup.Item>
                                ))
                            }
                        </ListGroup>
                        
                        {
                            isEditing && (
                                <Form.Control className="mt-2" as="textarea" rows={3} value={newContext} placeholder="Enter new context..." onChange={(event) => {
                                    setNewContext(event.target.value)
                                }} />
                            )
                        }

                        {
                            isLoadingContext ? (
                                <Row className="mt-2">
                                    <Col>
                                        <FontAwesomeIcon icon={faSpinner} spin /> Loading context...
                                    </Col>
                                </Row>
                            ) : isEditing ? (
                                <Row className="mt-2">
                                    <Col className="text-right">
                                        <Button variant="outline-secondary" style={{marginRight: '8px'}} size="sm" onClick={() => {cancelEdits()}}>
                                            Cancel
                                        </Button>
                                        {
                                            !isSavingContext ? (
                                                <Button variant="secondary" size="sm" onClick={() => {saveContext()}}>
                                                    Save Changes
                                                </Button>
                                            ) : (
                                                <Button variant="secondary" size="sm" disabled>
                                                    <FontAwesomeIcon icon={faSpinner} spin /> Saving...
                                                </Button>
                                            )
                                        }
                                    </Col>
                                </Row>
                            ) : (
                                <Row className="mt-2">
                                    <Col>
                                        <Button variant="outline-secondary" size="sm" onClick={() => {setIsEditing(true)}}>
                                            <FontAwesomeIcon icon={faPenToSquare} /> Edit Context
                                        </Button>
                                    </Col>
                                </Row>
                            )
                        }
                        <hr />
                        <Row className="mt-2">
                            <Col>
                                <Form.Label><small className="text-muted">Sync latest data before generating clinical summary?</small></Form.Label>
                                <small>
                                    <Form.Check type="switch" checked={shouldReloadContext} onChange={() => {setShouldReloadContext(!shouldReloadContext)}} label={shouldReloadContext ? "Sync Data" : "Use Lastest Cached Data"} />
                                </small>
                            </Col>
                        </Row>
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button size="sm" variant="outline-secondary" onClick={() => props.setShowModal(false)}>
                    Cancel
                </Button>
                <OverlayTrigger
                    placement="top"
                    overlay={
                        <Tooltip>
                            {isEditing ? "Finish editing before regeneration" : "Regenerate"}
                        </Tooltip>
                    }
                >
                    <Button size="sm" variant="primary" onClick={() => regenerate(props.target)} disabled={submitting || isEditing}>
                        {
                            submitting ? (
                                <FontAwesomeIcon icon={faSpinner} spin />
                            ) : (
                                <><FontAwesomeIcon icon={faRefresh} /> Regenerate</>
                            )
                        }
                    </Button>
                </OverlayTrigger>
            </Modal.Footer>
        </Modal>
    );
};
